// Thin wrapper over Chrome's built-in Prompt API (Gemini Nano). Used by the
// admin Source-of-Truth editor to extract an answer from a fetched page fully
// on-device. Feature-detects the current `LanguageModel` global and falls back
// to the older `window.ai.languageModel` surface. All calls are best-effort:
// callers should handle "no-api"/"unavailable" and degrade gracefully.

export type NanoStatus =
  | "available" // model ready
  | "downloadable" // supported, needs a one-time download
  | "downloading" // download in progress
  | "unavailable" // API present but this device can't run it
  | "no-api"; // browser has no Prompt API at all

/* eslint-disable @typescript-eslint/no-explicit-any */

function getLanguageModel(): any | null {
  if (typeof window === "undefined") return null;
  const w = window as any;
  if (w.LanguageModel) return w.LanguageModel;
  if (w.ai?.languageModel) return w.ai.languageModel;
  return null;
}

export async function nanoAvailability(): Promise<NanoStatus> {
  const lm = getLanguageModel();
  if (!lm) return "no-api";
  try {
    if (typeof lm.availability === "function") {
      const status = await lm.availability();
      if (status === "available" || status === "readily") return "available";
      if (status === "downloadable" || status === "after-download") return "downloadable";
      if (status === "downloading") return "downloading";
      return "unavailable";
    }
    // Older API: capabilities().available = "readily" | "after-download" | "no"
    if (typeof lm.capabilities === "function") {
      const caps = await lm.capabilities();
      if (caps?.available === "readily") return "available";
      if (caps?.available === "after-download") return "downloadable";
      return "unavailable";
    }
  } catch {
    return "unavailable";
  }
  return "unavailable";
}

export interface ExtractOptions {
  onProgress?: (pct: number) => void;
}

/**
 * Extract a concise, factual answer to `question` from `pageText` using Gemini
 * Nano, entirely on-device. Throws if the Prompt API is missing or the session
 * can't be created — callers should catch and fall back to the raw page text.
 */
export async function extractWithNano(
  pageText: string,
  question: string,
  opts: ExtractOptions = {},
): Promise<string> {
  const lm = getLanguageModel();
  if (!lm) throw new Error("Gemini Nano (Prompt API) is not available in this browser.");

  const createOpts: any = {
    monitor(m: any) {
      m.addEventListener?.("downloadprogress", (e: any) => {
        if (opts.onProgress && typeof e.loaded === "number") {
          opts.onProgress(Math.round(e.loaded * 100));
        }
      });
    },
  };

  let session: any;
  try {
    session = await lm.create(createOpts);
  } catch (e) {
    throw new Error(`Could not start Gemini Nano: ${(e as Error).message}`);
  }

  const prompt = `You are extracting a concise, factual answer from a web page for an internal knowledge base.

Question: ${question?.trim() || "Summarize the key facts on this page."}

Page content:
"""
${pageText}
"""

Write a clear, self-contained answer to the question using only facts stated on the page. Be factual and concise (no more than ~150 words). If the page does not answer the question, say what relevant information it does contain instead.`;

  try {
    const out = await session.prompt(prompt);
    return (out || "").trim();
  } finally {
    session.destroy?.();
  }
}
